import React, { FC, useEffect, useMemo, useState } from "react"
import { IncomingOptions, useFetch } from "use-http";
import { api, makeHeaders } from "../../util/api";
import LTable from "../../components/LTable";
import { Card, Td, Tr } from "@chakra-ui/react";
import { LHeader } from "../../components/LTable/LHead";
import { SectionAPI } from "./types";
import { usePagination } from "../../hooks/usePagination";
import { isSafe } from "../../util/array";
import { sortSections } from "../../util/section";
import SectionsSummary from "./summary";
import { formatNumber } from "../../util/string";
import LCell from "../../components/LTable/LCell";
import { ERROR_MESSAGE, useNotify } from "../../hooks/useNotify";

const Sections: FC = () => {
  const notify = useNotify()
  const [rawSections, setRawSections] = useState<SectionAPI[]>([]);
  const [sort, setSort] = useState<{ by: string; direction: 'asc' | 'desc' }>({
    by: '',
    direction: 'asc',
  });
  const sortHandler = (head: string, direction: 'asc' | 'desc') => {
    setSort({ by: head, direction });
  };

  const sortedSections = useMemo(() => {
    return sortSections(rawSections, sort.by, sort.direction);
  }, [rawSections, sort]);


  const {
    get: fetchSections,
    cache,
    loading,
    response,
    error,
  } = useFetch(`${api}/sections`, makeHeaders() as IncomingOptions);

  const rowsPerPageOptions = [10, 25, 50, 100];

  const {
    data: sections,
    page,
    rowsPerPage,
    setRowsPerPage,
    nextPage,
    prevPage,
    canPrevPage,
    canNextPage,
  } = usePagination(sortedSections, rowsPerPageOptions[0]);

  const columns: LHeader[] = [{
    label: 'Sección',
    value: 'sectionNumber',
  }, {
    label: 'Lista nominal',
    value: 'nominalList',
  }, {
    label: 'L. N. Hombres',
    value: 'nominalListMen',
  }, {
    label: 'L. N. Mujeres',
    value: 'nominalListWomen',
  }, {
    label: 'Padrón electoral',
    value: 'electoralRoll',
  }, {
    label: 'P. E. Hombres',
    value: 'electoralRollMen',
  }, {
    label: 'P. E. Mujeres',
    value: 'electoralRollWomen',
  }];

  const refreshSections = async () => {
    cache.clear();
    const updatedSections = await fetchSections();
    if (response.ok) {
      setRawSections(isSafe(updatedSections, error))
    } else {
      notify(ERROR_MESSAGE, 'error');
    }
  };

  useEffect(() => {
    refreshSections(); // eslint-disable-next-line
  }, []);

  return (
    <>
      <SectionsSummary />
      <Card gap={8}>
        <LTable
          loading={loading}
          columns={columns}
          paginable
          page={page}
          rowsPerPage={rowsPerPage}
          totalItems={rawSections.length}
          pageOptions={rowsPerPageOptions}
          onRowsPerPageChange={setRowsPerPage}
          onNextPage={nextPage}
          onPrevPage={prevPage}
          canPrevPage={canPrevPage}
          canNextPage={canNextPage}
          sortable
          onSort={sortHandler}
        >
          {!sections.length && (
            <Tr>
              <Td fontSize='sm' fontWeight='medium' color='gray.500' colSpan={columns.length} textAlign='center'>
                { loading && 'Cargando...'}
                { !loading && 'No hay datos para mostrar.'}
              </Td>
            </Tr>
          )}
          {sections.map((section: SectionAPI) => (
            <Tr key={section.id}>
              <LCell>{section.sectionNumber}</LCell>
              <LCell>{formatNumber(section.nominalList)}</LCell>
              <LCell>{formatNumber(section.nominalListMen)}</LCell>
              <LCell>{formatNumber(section.nominalListWomen)}</LCell>
              <LCell>{formatNumber(section.electoralRoll)}</LCell>
              <LCell>{formatNumber(section.electoralRollMen)}</LCell>
              <LCell>{formatNumber(section.electoralRollWomen)}</LCell>
            </Tr>
          ))}
        </LTable>
      </Card>
    </>

  );
}

export default Sections
